import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionInputHandle } from '@bonusly/common-action-input';
import { useAllowanceBoostId } from '@bonusly/common-rewards';
import { useEventListener } from '@bonusly/events';
import { Recipient } from '@bonusly/recipient-entities';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { GiveBoxEvent } from '../events';
import { createRecipientContent } from '../utils/content';
import { parseErrorMessage } from '../utils/error';
import { useBonusBoxAnalytics } from './use-bonus-box-analytics';
import { useBudget } from './use-budget';
import { useGiveRecognition } from './use-give-recognition';
import { usePeerToPeerModules } from './use-peer-to-peer-modules';
import { useSharedConfig } from './use-shared-config';
import { useValidator } from './use-validator';

interface UseGiveBoxOptions {
	ignoreInitEvent?: boolean;
	onSuccess?: VoidFunction;
	via?: string;
	msTeamsMeetingId?: string;
	draftAssistInteractionIds?: string[];
}

export const useGiveBox = ({ ignoreInitEvent = false, onSuccess, via, msTeamsMeetingId, draftAssistInteractionIds }: UseGiveBoxOptions = {}) => {
	const inputRef = useRef<ActionInputHandle>(null);
	const analytics = useBonusBoxAnalytics();
	const { id: allowanceBoostId } = useAllowanceBoostId();
	const handlers = useSharedConfig({ includeGroups: true });
	const modules = usePeerToPeerModules();
	const { t } = useTranslation('recognition');
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const [wrapPost, setWrapPost] = useState(true);

	// Listen for an event to give to a specific user
	// On received, populate the input with the user's name and focus the input
	// NOTE: This can be removed once the give box can only be reached via the modal
	const initGiveToUser = useCallback(
		(data: { recipient: Recipient }) => {
			if (ignoreInitEvent) {
				return;
			}

			if (data && data.recipient) {
				inputRef.current?.insert(createRecipientContent(data.recipient));
			}

			inputRef.current?.focus();
		},
		[ignoreInitEvent]
	);

	useEventListener(GiveBoxEvent.Open, initGiveToUser);

	// Create props to pass into the ActionInput
	const budget = useBudget();
	const validator = useValidator();
	const { isSubmitting, onSubmit } = useGiveRecognition({
		wrapPost,
		draftAssistInteractionIds,
		onError: (err: unknown) => {
			const message = parseErrorMessage(err);
			inputRef.current?.error(message);
		},
		onSuccess: () => {
			setWrapPost(true);
			onSuccess?.();
			inputRef.current?.reset();
		},
		via,
		msTeamsMeetingId,
	});

	return {
		allowanceBoostId,
		budget,
		context: 'recognition',
		isSubmitting,
		modules,
		placeholder: t(isBizyEnabledForUser ? 'bonus_input.placeholder_with_bizy_hint' : 'bonus_input.placeholder'),
		onSubmit,
		onStartDraft: analytics.trackBonusStart,
		ref: inputRef,
		validator,
		wrapPost,
		setWrapPost,
		...handlers,
	};
};
